import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Bell, Check, CheckCheck, Users, ClipboardList, Workflow } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { format } from 'date-fns';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Polling interval: 60 seconds
const POLL_INTERVAL = 60000;

export default function NotificationPanel() {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const { getAuthHeader } = useAuth();
  
  // Track previous count to detect new notifications
  const previousCountRef = useRef(0);
  const isFirstLoadRef = useRef(true);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/notifications/unread-count`, {
        headers: getAuthHeader()
      });
      const newCount = response.data.count || 0;
      
      // Show toast for new notifications (not on first load)
      if (!isFirstLoadRef.current && newCount > previousCountRef.current) {
        const diff = newCount - previousCountRef.current;
        toast.info(`You have ${diff} new notification${diff > 1 ? 's' : ''}`, {
          duration: 5000,
          action: {
            label: 'View',
            onClick: () => setIsOpen(true)
          }
        });
      }
      
      previousCountRef.current = newCount;
      isFirstLoadRef.current = false;
      setUnreadCount(newCount);
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  }, [getAuthHeader]);

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/notifications?limit=30&include_acknowledged=false`, {
        headers: getAuthHeader()
      });
      const notifs = response.data.notifications || response.data || [];
      setNotifications(notifs);
      setUnreadCount(notifs.filter(n => !n.acknowledged && !n.is_read).length);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  const acknowledgeNotification = async (notificationId) => {
    try {
      await axios.post(
        `${API_URL}/notifications/${notificationId}/acknowledge`,
        {},
        { headers: getAuthHeader() }
      );
      // Update local state
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      setUnreadCount(prev => Math.max(0, prev - 1));
      previousCountRef.current = Math.max(0, previousCountRef.current - 1);
    } catch (error) {
      console.error('Error acknowledging notification:', error);
      toast.error('Failed to mark as read');
    }
  };

  const acknowledgeAll = async () => {
    try {
      const response = await axios.post(
        `${API_URL}/notifications/acknowledge-all`,
        {},
        { headers: getAuthHeader() }
      );
      setNotifications([]);
      setUnreadCount(0);
      previousCountRef.current = 0;
      toast.success(`Marked ${response.data.acknowledged_count} notification(s) as read`);
    } catch (error) {
      console.error('Error acknowledging all:', error);
      toast.error('Failed to mark all as read');
    }
  };

  // Initial fetch and polling for count
  useEffect(() => {
    fetchUnreadCount();
    
    const interval = setInterval(() => {
      fetchUnreadCount();
    }, POLL_INTERVAL);
    
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  // Fetch full list when popover opens
  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
    }
  }, [isOpen, fetchNotifications]);

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'delegation':
        return <Users className="h-4 w-4 text-blue-500 flex-shrink-0" />;
      case 'checklist':
        return <ClipboardList className="h-4 w-4 text-green-500 flex-shrink-0" />;
      case 'sws':
        return <Workflow className="h-4 w-4 text-purple-500 flex-shrink-0" />;
      default:
        return <Bell className="h-4 w-4 text-gray-500 flex-shrink-0" />;
    }
  };

  const getNotificationBgColor = (type) => {
    switch (type) {
      case 'delegation':
        return 'bg-blue-50 hover:bg-blue-100 border-blue-200';
      case 'checklist':
        return 'bg-green-50 hover:bg-green-100 border-green-200';
      case 'sws':
        return 'bg-purple-50 hover:bg-purple-100 border-purple-200';
      default:
        return 'bg-gray-50 hover:bg-gray-100 border-gray-200';
    }
  };

  const formatTimeAgo = (dateStr) => {
    try {
      const date = new Date(dateStr);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);
      
      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      if (diffDays < 7) return `${diffDays}d ago`;
      return format(date, 'MMM dd');
    } catch {
      return '';
    }
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button 
          variant="ghost" 
          size="icon" 
          className="relative" 
          data-testid="notification-bell"
        >
          <Bell className="w-5 h-5" />
          {unreadCount > 0 && (
            <Badge
              className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 bg-red-500 hover:bg-red-500 text-white text-xs"
              data-testid="unread-count"
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-96 p-0" align="end">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h3 className="font-semibold">Notifications</h3>
          {notifications.length > 0 && (
            <Button 
              variant="ghost" 
              size="sm" 
              className="text-xs text-muted-foreground h-auto p-1"
              onClick={acknowledgeAll}
            >
              <CheckCheck className="h-3 w-3 mr-1" />
              Mark all read
            </Button>
          )}
        </div>
        
        {/* Notification List */}
        <ScrollArea className="h-[400px]">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
            </div>
          ) : notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <Bell className="h-10 w-10 mb-3 opacity-30" />
              <p className="text-sm">No new notifications</p>
              <p className="text-xs mt-1">You're all caught up!</p>
            </div>
          ) : (
            <div className="divide-y">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  data-testid={`notification-${notification.id}`}
                  className={`flex items-start gap-3 p-3 ${getNotificationBgColor(notification.type)} border-l-4 transition-colors cursor-pointer`}
                >
                  <div className="mt-0.5">
                    {getNotificationIcon(notification.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h4 className="font-medium text-sm truncate">{notification.title}</h4>
                      <Badge variant="outline" className="text-[10px] px-1 py-0 h-4">
                        {notification.type}
                      </Badge>
                    </div>
                    <p className="text-xs text-gray-600 mt-0.5 line-clamp-2">
                      {notification.message}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {formatTimeAgo(notification.created_at)}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 shrink-0 hover:bg-white/50"
                    onClick={(e) => {
                      e.stopPropagation();
                      acknowledgeNotification(notification.id);
                    }}
                    title="Mark as read"
                  >
                    <Check className="h-3 w-3" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
        
        {/* Footer */}
        {notifications.length > 0 && (
          <div className="border-t px-4 py-2 text-center bg-gray-50">
            <p className="text-xs text-muted-foreground">
              {unreadCount} unread notification{unreadCount !== 1 ? 's' : ''}
            </p>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
